import type { MouseEvent } from "react";

import { useRef, useState, useEffect } from "react";
import {
	MoreVertical,
	PlusCircle,
	Pencil,
	Trash2,
	FileText
} from "lucide-react";
import pluralize from "pluralize";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import { useDeleteObservationCategory } from "src/features/Admin/ObservationsCategories/hooks/useDeleteObservationCategory";
import { useNavigate, useSearchParams } from "react-router-dom";

export type CategoryCardProps = {
	id: number;

	name: string;

	publishedFormsCount: number;

	draftFormsCount: number;

	showCardOptions?: boolean;

	admin?: boolean;

	onEditNewCategoryClick?: () => void;
};

const CategoryCard = ({
	id,
	name,
	publishedFormsCount,
	draftFormsCount,
	showCardOptions,
	admin,
	onEditNewCategoryClick
}: CategoryCardProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

	const {
		mutate: deleteObservationCategory,
		isLoading: isDeletingObservationCategory
	} = useDeleteObservationCategory();

	const [, setSearchParams] = useSearchParams();
	const navigate = useNavigate();

	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleOutsideClick = (e: globalThis.MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	const toggleMenu = (e: MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsOpen((prev) => !prev);
	};

	const totalForms = publishedFormsCount + draftFormsCount;

	return (
		<>
			<div className="group rounded-xl border border-border-light bg-white p-5 shadow-sm hover:shadow-md hover:-translate-y-px transition-all duration-200 w-full">
				<div className="flex items-start justify-between gap-3">
					<div className="flex-1 min-w-0">
						<h3 className="font-heading text-base font-medium text-zinc-800 line-clamp-2 mb-3">
							{name}
						</h3>

						<div className="flex items-center gap-4">
							<div className="flex items-center gap-1.5 text-[13px] text-zinc-500">
								<FileText className="h-3.5 w-3.5" />
								<span>
									{publishedFormsCount} {pluralize("form", publishedFormsCount)}
								</span>
							</div>

							{admin && draftFormsCount > 0 && (
								<span className="text-[13px] text-zinc-400">
									{draftFormsCount} {pluralize("draft", draftFormsCount)}
								</span>
							)}
						</div>
					</div>

					{showCardOptions && (
						<div className="relative" ref={ref}>
							<button
								type="button"
								className="rounded-lg p-1.5 hover:bg-muted transition-colors opacity-0 group-hover:opacity-100"
								onClick={toggleMenu}
								aria-label="Category options"
							>
								<MoreVertical className="h-4 w-4 text-zinc-400" />
							</button>

							{isOpen && (
								<div
									className="absolute right-0 top-full z-10 mt-1 min-w-[180px] rounded-lg border border-border bg-white shadow-lg py-1"
									aria-label="menu-options"
								>
									<button
										type="button"
										className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-zinc-700 hover:bg-primary-lighter transition-colors"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											setIsOpen(false);
											navigate(
												`/observations/forms/new-observation-form?categoryId=${id}`
											);
										}}
									>
										<PlusCircle className="h-4 w-4 text-zinc-400" />
										Create new form
									</button>

									<button
										type="button"
										className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-zinc-700 hover:bg-primary-lighter transition-colors"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											setIsOpen(false);
											setSearchParams((searchParams) => {
												searchParams.set("categoryId", id.toString());
												return searchParams;
											});
											onEditNewCategoryClick?.();
										}}
									>
										<Pencil className="h-4 w-4 text-zinc-400" />
										Edit category
									</button>

									<div className="my-1 border-t border-border" />

									<button
										type="button"
										className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											setIsOpen(false);
											setIsConfirmationModalOpen(true);
										}}
									>
										<Trash2 className="h-4 w-4" />
										Delete category
									</button>
								</div>
							)}
						</div>
					)}
				</div>
			</div>

			<ConfirmationModal
				isOpen={isConfirmationModalOpen}
				title={`Delete "${name}" category?`}
				description={
					<p className="text-center text-sm text-zinc-600">
						All forms in this category will be moved to the
						&quot;Undefined&quot; category.
					</p>
				}
				onCancel={() => setIsConfirmationModalOpen(false)}
				onSubmit={() => {
					deleteObservationCategory({ id });
					setIsConfirmationModalOpen(false);
				}}
				submitBtnProps={{
					children: "Delete",
					isLoading: isDeletingObservationCategory
				}}
				cancelBtnProps={{
					children: "Cancel"
				}}
			/>
		</>
	);
};

export default CategoryCard;
